import axios from "axios";
import {
  getAuthSession,
  GETSINGLEFULLCONFIGS,
  PRINTJARALLOWED,
  getPreUrlAccordingToCart
} from "../../constants/constant";

const DEFAULT_AGENT_PORT = 17312;
const SILENT_DESKTOP = "SILENT_DESKTOP";
const encoder = new TextEncoder();
const configCache = new Map();
const connections = { ble: null, serial: null, usb: null };
const SILENT_PRINT_DECISION_EVENT = "snp-silent-print-decision";

const PRINTER_PROFILES = {
  "58mm-384": { columns: 32, dots: 384 },
  // Both 58 mm profiles use the portable 32-column text grid. Some 432-dot
  // printers still use 12-dot Font A and wrap a nominal 36th column.
  "58mm-432": { columns: 32, dots: 432 },
  "76mm-512": { columns: 42, dots: 512 },
  "80mm-512": { columns: 42, dots: 512 },
  "80mm-576": { columns: 48, dots: 576 },
  "112mm-832": { columns: 69, dots: 832 },
};

const TEXT_SIZE_BYTES = {
  normal: 0x00,
  tall: 0x01,
  wide: 0x10,
  big: 0x11,
  "big-2": 0x22,
  "big-3": 0x33,
  "big-4": 0x44,
  "big-5": 0x55,
  "big-6": 0x66,
};

const concatBytes = (...parts) => {
  const valid = parts.filter(Boolean);
  const output = new Uint8Array(valid.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  valid.forEach((part) => { output.set(part, offset); offset += part.length; });
  return output;
};

const toBase64 = (bytes) => {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + 0x8000, bytes.length)));
  }
  return btoa(binary);
};

const stripMarkup = (value) => String(value || "")
  .replace(/<img>.*?<\/img>/gi, "")
  .replace(/<\/?(?:b|u|lang-hi)>/gi, "")
  .replace(/<\/?font(?:\s+[^>]*)?>/gi, "")
  .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

const textStyle = (value) => {
  const source = String(value || "");
  const size = /size=['"](big(?:-[2-6])?|wide|tall|normal)['"]/i.exec(source)?.[1] || "normal";
  return {
    text: stripMarkup(source).trim(),
    bold: /<b>/i.test(source),
    underline: /<u>/i.test(source),
    size,
    widthScale: size === "wide" || size === "big" ? 2
      : /^big-([2-6])$/.test(size) ? Number(size.slice(4)) + 1 : 1,
  };
};

const wrapText = (value, width) => {
  const text = String(value || "").trim();
  const limit = Math.max(1, width);
  if (!text) return [""];
  const lines = [];
  let remaining = text;
  while (remaining.length > limit) {
    let splitAt = remaining.lastIndexOf(" ", limit);
    if (splitAt < 1) splitAt = limit;
    lines.push(remaining.slice(0, splitAt).trimEnd());
    remaining = remaining.slice(splitAt).trimStart();
  }
  lines.push(remaining);
  return lines;
};

const parseTaggedColumns = (line) => {
  const source = String(line || "");
  const tags = [...source.matchAll(/\[([LCR])\]/g)];
  if (!tags.length) return [{ align: "L", ...textStyle(source) }];
  return tags.map((tag, index) => ({
    align: tag[1],
    ...textStyle(source.slice(tag.index + tag[0].length, tags[index + 1]?.index ?? source.length)),
  }));
};

const columnWidths = (segments, columns) => {
  const count = segments.length;
  if (count <= 1) return [columns];
  if (count === 2) {
    const requiredRight = segments[1].text.length * segments[1].widthScale;
    const minimumLeft = Math.min(14, Math.max(10, segments[0].text.length * segments[0].widthScale));
    const right = Math.min(columns - minimumLeft, Math.max(8, requiredRight));
    return [columns - right, right];
  }
  if (count === 4) {
    const item = Math.floor(columns * 0.4);
    const quantity = Math.max(5, Math.floor(columns * 0.16));
    const remaining = columns - item - quantity;
    const price = Math.floor(remaining / 2);
    return [item, quantity, price, columns - item - quantity - price];
  }
  const first = Math.floor(columns * 0.5);
  const remaining = columns - first;
  const base = Math.floor(remaining / (count - 1));
  const widths = [first];
  for (let index = 1; index < count; index += 1) {
    widths.push(index === count - 1 ? columns - widths.reduce((sum, width) => sum + width, 0) : base);
  }
  return widths;
};

const styleCommand = (segment) => new Uint8Array([
  0x1b, 0x45, segment.bold ? 1 : 0,
  0x1b, 0x2d, segment.underline ? 1 : 0,
  0x1d, 0x21, TEXT_SIZE_BYTES[segment.size] ?? 0,
]);

const NORMAL_STYLE = new Uint8Array([0x1b, 0x45, 0, 0x1b, 0x2d, 0, 0x1d, 0x21, 0]);

const appendTextLine = (output, line, columns) => {
  const segments = parseTaggedColumns(line);
  if (segments.length === 1) {
    const segment = segments[0];
    if (/^-{3,}$/.test(segment.text)) segment.text = "-".repeat(columns);
    const capacity = Math.max(1, Math.floor(columns / segment.widthScale));
    const wrapped = wrapText(segment.text, capacity);
    const alignment = segment.align === "C" ? 1 : segment.align === "R" ? 2 : 0;
    wrapped.forEach((part) => {
      output.push(new Uint8Array([0x1b, 0x61, alignment]));
      output.push(styleCommand(segment), encoder.encode(part), NORMAL_STYLE, encoder.encode("\n"));
    });
    output.push(new Uint8Array([0x1b, 0x61, 0]));
    return;
  }

  const widths = columnWidths(segments, columns);
  const wrapped = segments.map((segment, index) => wrapText(
    segment.text,
    Math.max(1, Math.floor(widths[index] / segment.widthScale))
  ));
  const rowCount = Math.max(...wrapped.map((parts) => parts.length));
  for (let row = 0; row < rowCount; row += 1) {
    segments.forEach((segment, index) => {
      const part = wrapped[index][row] || "";
      const usedWidth = part.length * segment.widthScale;
      const freeWidth = Math.max(0, widths[index] - usedWidth);
      const leftPadding = segment.align === "R" ? freeWidth : segment.align === "C" ? Math.floor(freeWidth / 2) : 0;
      const rightPadding = freeWidth - leftPadding;
      if (leftPadding) output.push(NORMAL_STYLE, encoder.encode(" ".repeat(leftPadding)));
      if (part) output.push(styleCommand(segment), encoder.encode(part));
      if (rightPadding) output.push(NORMAL_STYLE, encoder.encode(" ".repeat(rightPadding)));
    });
    output.push(NORMAL_STYLE, encoder.encode("\n"));
  }
};

const imageToRasterCommand = (image, printerWidth, maxHeight, alignment) => {
  const scale = Math.min(1, printerWidth / image.naturalWidth, maxHeight / image.naturalHeight);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.fillStyle = "#fff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  const luminance = new Float32Array(width * height);
  for (let index = 0; index < luminance.length; index += 1) {
    const offset = index * 4;
    const alpha = pixels[offset + 3] / 255;
    luminance[index] = ((pixels[offset] * 0.299) + (pixels[offset + 1] * 0.587)
      + (pixels[offset + 2] * 0.114)) * alpha + (255 * (1 - alpha));
  }

  // Match the Android app's gradient conversion. Error diffusion preserves
  // light logo detail that a single fixed threshold drops completely.
  const imageWidthBytes = Math.ceil(width / 8);
  const printerWidthBytes = Math.ceil(printerWidth / 8);
  const leftPaddingBytes = alignment === 2
    ? printerWidthBytes - imageWidthBytes
    : alignment === 1 ? Math.floor((printerWidthBytes - imageWidthBytes) / 2) : 0;
  const raster = new Uint8Array(printerWidthBytes * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width) + x;
      const oldPixel = luminance[index];
      const newPixel = oldPixel < 160 ? 0 : 255;
      if (newPixel === 0) raster[(y * printerWidthBytes) + leftPaddingBytes + Math.floor(x / 8)] |= 0x80 >> (x % 8);
      const error = oldPixel - newPixel;
      if (x + 1 < width) luminance[index + 1] += error * 7 / 16;
      if (y + 1 < height) {
        if (x > 0) luminance[index + width - 1] += error * 3 / 16;
        luminance[index + width] += error * 5 / 16;
        if (x + 1 < width) luminance[index + width + 1] += error / 16;
      }
    }
  }
  return concatBytes(new Uint8Array([
    0x1d, 0x76, 0x30, 0x00,
    printerWidthBytes & 0xff, (printerWidthBytes >> 8) & 0xff,
    height & 0xff, (height >> 8) & 0xff,
  ]), raster, new Uint8Array([0x0a]));
};

const decodeReceiptImage = (base64, contentType, printerWidth, maxHeight, alignment) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(imageToRasterCommand(image, printerWidth, maxHeight, alignment));
  image.onerror = () => reject(new Error("Receipt image could not be decoded."));
  image.src = String(base64 || "").startsWith("data:")
    ? base64
    : `data:${contentType || "image/jpeg"};base64,${base64}`;
});

export const buildTaggedEscPos = async (taggedText, channelConfig, responseData = {}) => {
  const profile = PRINTER_PROFILES[channelConfig.profile];
  const columns = profile?.columns
    || Math.max(24, Math.round((Number(channelConfig.paperWidthMm) || 80) * 0.6));
  const rasterWidth = profile?.dots || Number(channelConfig.rasterWidth) || 576;
  const output = [new Uint8Array([0x1b, 0x40])];
  for (const line of String(taggedText || "").replace(/\r\n/g, "\n").split("\n")) {
    const imageKey = /<img>([^<]+)<\/img>/i.exec(line)?.[1]?.trim();
    if (imageKey) {
      const encodedImage = responseData[`${imageKey}_base64`];
      if (encodedImage) {
        const alignment = line.includes("[C]") ? 1 : line.includes("[R]") ? 2 : 0;
        output.push(new Uint8Array([0x1b, 0x61, alignment]));
        output.push(await decodeReceiptImage(
          encodedImage,
          responseData[`${imageKey}_content_type`],
          rasterWidth,
          imageKey === "a2z_qr_snp" ? 370 : 256,
          alignment
        ));
        output.push(new Uint8Array([0x1b, 0x61, 0]));
      }
      continue;
    }
    appendTextLine(output, line, columns);
  }
  output.push(NORMAL_STYLE, new Uint8Array([0x1b, 0x61, 0, 0x1b, 0x64, 4]));
  if (channelConfig.cutMode !== "none") {
    output.push(new Uint8Array([0x1d, 0x56, channelConfig.cutMode === "full" ? 0 : 1]));
  }
  return concatBytes(...output);
};

const requestId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

const normalizeAgentPort = (value) => {
  const port = Number(value) || DEFAULT_AGENT_PORT;
  if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error("Printer Agent port must be between 1 and 65535.");
  return port;
};

const agentRequest = async (agentPort, path, body, pair = false) => {
  const port = normalizeAgentPort(agentPort);
  try {
    const response = await fetch(`http://127.0.0.1:${port}${path}`, {
      method: body ? "POST" : "GET",
      cache: "no-store",
      targetAddressSpace: "loopback",
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(pair ? { "X-SNP-Pair": "confirm" } : {}),
        "X-Request-ID": requestId(),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || `Printer Agent HTTP ${response.status}`);
    return payload;
  } catch (error) {
    if (error instanceof TypeError || /failed to fetch/i.test(error?.message || "")) {
      throw new Error("Cannot reach SNP Printer Agent, Download and start the Agent if not, confirm its port, and allow Apps on device in the browser.");
    }
    throw error;
  }
};

const ensureAgent = async (agentPort) => {
  const health = await agentRequest(agentPort, "/health");
  if (!health.paired) await agentRequest(agentPort, "/pair", {}, true);
};

const parseHex = (value, label) => {
  const normalized = String(value || "").trim().replace(/^0x/i, "");
  if (!/^[0-9a-f]+$/i.test(normalized)) throw new Error(`${label} is invalid.`);
  return Number.parseInt(normalized, 16);
};

const connectBle = async (config, allowPrompt = true) => {
  if (!window.isSecureContext || !navigator.bluetooth) throw new Error("Web Bluetooth is unavailable.");
  const granted = navigator.bluetooth.getDevices ? await navigator.bluetooth.getDevices() : [];
  const device = granted[0] || (allowPrompt ? await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: [config.serviceUuid],
  }) : null);
  if (!device) throw new Error("BLE printer is not paired. Connect it from Printer Configuration first.");
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(config.serviceUuid);
  connections.ble = { device, server, characteristic: await service.getCharacteristic(config.characteristicUuid) };
};

const connectSerial = async (config, allowPrompt = true) => {
  if (!window.isSecureContext || !navigator.serial) throw new Error("Web Serial is unavailable.");
  const ports = await navigator.serial.getPorts();
  const port = ports[0] || (allowPrompt ? await navigator.serial.requestPort() : null);
  if (!port) throw new Error("Serial printer is not paired. Connect it from Printer Configuration first.");
  if (!port.writable) await port.open({ baudRate: Number(config.baudRate) || 9600, bufferSize: 65536 });
  connections.serial = port;
};

const findUsbOutput = (device) => {
  for (const iface of device.configuration?.interfaces || []) {
    for (const alternate of iface.alternates || []) {
      const endpoint = alternate.endpoints?.find((candidate) => candidate.direction === "out");
      if (endpoint) return { interfaceNumber: iface.interfaceNumber, alternateSetting: alternate.alternateSetting, endpointNumber: endpoint.endpointNumber };
    }
  }
  return null;
};

const connectUsb = async (config, allowPrompt = true) => {
  if (!window.isSecureContext || !navigator.usb) throw new Error("WebUSB is unavailable.");
  const filter = { vendorId: parseHex(config.vendorId, "USB Vendor ID"), productId: parseHex(config.productId, "USB Product ID") };
  const granted = await navigator.usb.getDevices();
  const device = granted.find((item) => item.vendorId === filter.vendorId && item.productId === filter.productId)
    || (allowPrompt ? await navigator.usb.requestDevice({ filters: [filter] }) : null);
  if (!device) throw new Error("USB printer is not paired. Connect it from Printer Configuration first.");
  if (!device.opened) await device.open();
  if (!device.configuration) await device.selectConfiguration(device.configurations?.[0]?.configurationValue || 1);
  const output = findUsbOutput(device);
  if (!output) throw new Error("No writable USB endpoint was found.");
  await device.claimInterface(output.interfaceNumber);
  if (output.alternateSetting) await device.selectAlternateInterface(output.interfaceNumber, output.alternateSetting);
  connections.usb = { device, ...output };
};

const sendDirect = async (type, bytes, config) => {
  if (type === "ble") {
    if (!connections.ble?.server?.connected) await connectBle(config, false);
    const characteristic = connections.ble.characteristic;
    for (let offset = 0; offset < bytes.length; offset += 180) {
      const chunk = bytes.slice(offset, offset + 180);
      if (characteristic.writeValueWithoutResponse) await characteristic.writeValueWithoutResponse(chunk);
      else await characteristic.writeValue(chunk);
    }
    return;
  }
  if (type === "serial") {
    if (!connections.serial?.writable) await connectSerial(config, false);
    const writer = connections.serial.writable.getWriter();
    try { await writer.write(bytes); } finally { writer.releaseLock(); }
    return;
  }
  if (type === "usb") {
    if (!connections.usb?.device?.opened) await connectUsb(config, false);
    for (let offset = 0; offset < bytes.length; offset += 16384) {
      const result = await connections.usb.device.transferOut(connections.usb.endpointNumber, bytes.slice(offset, offset + 16384));
      if (result.status !== "ok") throw new Error(`USB transfer failed: ${result.status}`);
    }
  }
};

const sendEscPos = async (type, bytes, config) => {
  if (type === "tcp" || type === "windows") {
    await ensureAgent(config.agentPort);
    const body = type === "tcp"
      ? { host: config.ip, port: Number(config.port) || 9100, timeoutMs: 5000, dataBase64: toBase64(bytes) }
      : { printerName: config.printerName, documentName: "SNP Billing ESC/POS", dataBase64: toBase64(bytes) };
    await agentRequest(config.agentPort, type === "tcp" ? "/api/tcp/send" : "/api/windows/send", body);
    return;
  }
  await sendDirect(type, bytes, config);
};

const loadConfig = async (baseUrl, shopHash, force = false) => {
  const key = `${baseUrl}|${shopHash}`;
  const cached = configCache.get(key);
  if (!force && cached && Date.now() - cached.loadedAt < 60_000) return cached.value;
  const prefix = getPreUrlAccordingToCart(getAuthSession().cartType);
  const url = `${baseUrl}${prefix}${GETSINGLEFULLCONFIGS}?adminId=${encodeURIComponent(getAuthSession().apiToken)}&configName=${encodeURIComponent(PRINTJARALLOWED)}&shopHash=${encodeURIComponent(shopHash)}`;
  const { data } = await axios.get(url);
  const value = data?.jcJson || {};
  configCache.set(key, { value, loadedAt: Date.now() });
  return value;
};

const channelConfig = (map, channel) => ({
  enabled: map[`${channel}_enabled`] === true,
  type: String(map[`${channel}_type`] || "tcp").toLowerCase(),
  profile: map[`${channel}_profile`] || "80mm-576",
  paperWidthMm: map[`${channel}_paper_width_mm`],
  rasterWidth: map[`${channel}_raster_width`],
  cutMode: map[`${channel}_cut_mode`] || "partial",
  copies: Math.max(1, Math.min(10, Number(map[`${channel}_copies`]) || 1)),
  ip: map[`${channel}_ip`],
  port: map[`${channel}_port`],
  printerName: map[`${channel}_printer_name`],
  vendorId: map[`${channel}_vendor_id`],
  productId: map[`${channel}_product_id`],
  serviceUuid: map[`${channel}_service_uuid`],
  characteristicUuid: map[`${channel}_characteristic_uuid`],
  baudRate: map[`${channel}_baud_rate`],
  agentPort: normalizeAgentPort(map.silent_agent_port),
});

export const prepareSilentPrinter = async (map, channel) => {
  const config = channelConfig(map || {}, channel);
  if (!config.enabled) throw new Error("Enable this printer channel first.");
  if (config.type === "tcp" || config.type === "windows") {
    await ensureAgent(config.agentPort);
    if (config.type === "tcp") {
      await agentRequest(config.agentPort, "/api/tcp/check", { host: config.ip, port: Number(config.port) || 9100, timeoutMs: 5000 });
    } else {
      await agentRequest(config.agentPort, "/api/windows/check", { printerName: config.printerName, documentName: "SNP Billing ESC/POS" });
    }
    return "SNP Printer Agent and printer are connected.";
  }
  if (config.type === "ble") await connectBle(config);
  else if (config.type === "serial") await connectSerial(config);
  else if (config.type === "usb") await connectUsb(config);
  else throw new Error(`Unsupported printer type: ${config.type}`);
  return `${config.type.toUpperCase()} printer is connected for this browser session.`;
};

export const listSilentWindowsPrinters = async (map) => {
  const agentPort = normalizeAgentPort(map?.silent_agent_port);
  await ensureAgent(agentPort);
  return agentRequest(agentPort, "/api/windows/printers");
};

export const inferSilentChannel = (name = "") => {
  const value = String(name).toLowerCase();
  if (value.includes("gen")) return "gen";
  if (value.includes("kot_item")) return "kot_item";
  if (value.includes("kot_previous")) return "kot_previous";
  if (value.includes("kot")) return "kot";
  if (value.includes("shop")) return "shop";
  return "user";
};

const requestPrintDecision = (options) => new Promise((resolve) => {
  window.dispatchEvent(new CustomEvent(SILENT_PRINT_DECISION_EVENT, {
    detail: { ...options, resolve },
  }));
});

const attemptDesktopPrint = async ({ baseUrl, shopHash, billNo, channel, silentData, forceConfig = false }) => {
    const record = await loadConfig(baseUrl, shopHash, forceConfig);
    if (String(record.val || "").toUpperCase() !== SILENT_DESKTOP) return false;
    const config = channelConfig(record.map || {}, channel);
    if (!config.enabled) return false;
    await ensureAgent(config.agentPort);
    const { data } = await axios.post(`${baseUrl}billing/print-bill-as-text`, {
      jcJson: {
        apiToken: getAuthSession().apiToken,
        shopHash,
        billNo,
        kot: channel.startsWith("kot"),
        shopPrint: channel === "shop",
        gen: channel === "gen",
        isMobile: false,
        silentPrinting: true,
        printerChannel: channel,
        ...silentData,
      },
    });
    const text = data?.jcJson?.textToPrint;
    if (!text) throw new Error("The server did not return printable ESC/POS text.");
    const bytes = await buildTaggedEscPos(text, config, data?.jcJson || {});
    for (let copy = 0; copy < config.copies; copy += 1) await sendEscPos(config.type, bytes, config);
    return true;
};

export const trySilentPrint = async ({ baseUrl, shopHash, billNo, channel, isMobile = false, silentData = {} }) => {
  if (!baseUrl || !shopHash || !billNo) return false;
  let record;
  try {
    record = await loadConfig(baseUrl, shopHash);
  } catch (error) {
    const decision = await requestPrintDecision({
      message: error?.message || "Printer configuration could not be loaded.",
      allowConfigure: true,
      retry: () => attemptDesktopPrint({ baseUrl, shopHash, billNo, channel, silentData, forceConfig: true }),
    });
    if (decision === "handled") return true;
    if (decision === "pdf") return false;
    return null;
  }
  const mode = String(record.val || "");

  // The existing mobile application URI is the direct mobile print transport.
  // Returning false continues into that route; browsers cannot report whether
  // an external custom-URI application completed the print.
  if (mode.toLowerCase() === "silent_mobile_tablet") return false;
  if (isMobile || mode.toUpperCase() !== SILENT_DESKTOP) return false;

  const config = channelConfig(record.map || {}, channel);
  if (!config.enabled) return false;

  try {
    return await attemptDesktopPrint({ baseUrl, shopHash, billNo, channel, silentData });
  } catch (error) {
    const retry = () => attemptDesktopPrint({
      baseUrl, shopHash, billNo, channel, silentData, forceConfig: true,
    });
    const decision = await requestPrintDecision({
      message: error?.message || "SNP Printer Agent or the configured printer is unavailable.",
      allowConfigure: true,
      retry,
    });
    if (decision === "handled") return true;
    if (decision === "pdf") return false;
    return null;
  }
};

